import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, PlusCircle, Save, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Chip, Field, Page, Section, Select, SuggestField } from "../components/wf";
import { api, money, num, todayISO } from "../lib/api";
import { useAuth, usePerms } from "../lib/auth";
import { useEmployees, useParts, useRefreshJobs, useSuggestions } from "../lib/queries";
import { DraftBanner, SaveStatus, useAutoSave, useDraft } from "../lib/autosave";

const TITLES = { work: "Work To Be Completed", parts: "Parts Used", hours: "Log Hours", expenses: "Daily Expense", hardware: "Extra Hardware", notes: "Monthly Note", daily_notes: "Daily Note" };
const blank = (kind, task) => ({
  date: todayISO(), work: "", description: "", parts_used: "", quantity: kind === "parts" ? "1" : "", note: "", hours: "8", num_techs: "", extra_names: "",
  cost: "", details: "", task_id: task ?? "", part_id: "", part_number: "", supplier: "", unit_cost: "", per_diem: "no", num_per_diems: "",
});

function Preview({ items, testid }) {
  return (
    <div className="mb-3 flex rounded-xl bg-wf-soft p-4 text-wf-softink" data-testid={testid}>
      {items.map(([l, v]) => (
        <div key={l} className="flex-1"><p className="text-xs font-semibold">{l}</p><p className="text-2xl font-extrabold">{v}</p></div>
      ))}
    </div>
  );
}

function PartPick({ parts, onPick }) {
  if (!parts?.length) return null;
  return (
    <Select label="Pick from Parts list" testid="entry-part-picker" value="" placeholder="Choose a saved part…"
      options={parts.map((p) => ({ value: p.id, label: [p.name, p.part_number].filter(Boolean).join(" · ") }))} onChange={(v) => v && onPick(parts.find((p) => p.id === v))} />
  );
}

export default function EntryForm() {
  const { wid, kind, eid } = useParams();
  const [params] = useSearchParams();
  const task = params.get("task");
  const nav = useNavigate();
  const qc = useQueryClient();
  const refresh = useRefreshJobs();
  const { seeMoney } = usePerms();
  const [f, setF] = useState(blank(kind, task));
  const [techIds, setTechIds] = useState([]);
  const [helperIds, setHelperIds] = useState([]);
  const { user } = useAuth();
  const [extraDates, setExtraDates] = useState([]);
  const [storedRate, setStoredRate] = useState(null);
  const [ready, setReady] = useState(!eid);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const emps = useEmployees(kind === "hours");
  const people = useQuery({ queryKey: ["users-assignable"], queryFn: () => api("/users-assignable"), enabled: kind === "work" });
  const perDiem = useQuery({ queryKey: ["per-diem"], queryFn: () => api("/settings/per-diem"), enabled: kind === "hours" });
  const parts = useParts(kind === "parts" || kind === "hardware");
  const wo = useQuery({ queryKey: ["work-order", wid], queryFn: () => api(`/work-orders/${wid}`) });
  const project = useQuery({ queryKey: ["project", wo.data?.project_id], queryFn: () => api(`/projects/${wo.data.project_id}`), enabled: kind === "hours" && !!wo.data?.project_id });
  const sSupplier = useSuggestions(kind === "parts" ? "supplier" : null);
  const sExpense = useSuggestions(kind === "expenses" ? "expense" : null);
  const sHardware = useSuggestions(kind === "hardware" ? "hardware" : null);

  useEffect(() => {
    const e = wo.data?.entries[kind]?.find((x) => x.id === eid);
    if (!e || ready) return;
    const s = (n) => (n ? String(n) : "");
    setF({
      date: e.date || todayISO(), work: e.work, description: e.description, parts_used: e.parts_used, quantity: s(e.quantity), note: e.note, hours: s(e.hours),
      num_techs: s(e.num_techs), extra_names: (e.extra_names ?? []).join(", "), cost: s(e.cost), details: e.details ?? "", task_id: e.task_id ?? "", part_id: e.part_id ?? "",
      part_number: e.part_number ?? "", supplier: e.supplier ?? "", unit_cost: s(e.unit_cost ?? 0), per_diem: e.per_diem ? "yes" : "no", num_per_diems: s(e.num_per_diems ?? 0),
    });
    setTechIds(e.tech_ids ?? []);
    setHelperIds(e.helper_ids ?? []);
    setStoredRate(e.per_diem ? e.per_diem_rate ?? null : null);
    setReady(true);
  }, [wo.data, kind, eid, ready]);

  const extraNames = f.extra_names.split(",").map((s) => s.trim()).filter(Boolean);
  const autoTechs = techIds.length + extraNames.length;
  const numTechs = parseInt(f.num_techs, 10) || autoTechs;
  const preview = useMemo(() => {
    const rates = (emps.data ?? []).filter((e) => techIds.includes(e.id)).map((e) => e.hourly_rate || 0);
    const avg = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
    const rateTotal = rates.reduce((a, b) => a + b, 0) + Math.max(numTechs - rates.length, 0) * avg;
    const hrs = parseFloat(f.hours) || 0;
    return { manHours: hrs * numTechs, cost: hrs * rateTotal };
  }, [emps.data, techIds, numTechs, f.hours]);
  const wantsPerDiem = f.per_diem === "yes";
  const numPerDiems = parseInt(f.num_per_diems, 10) || 0;
  const perDiemRate = storedRate ?? perDiem.data?.amount ?? 0;

  const build = () => {
    const body = { date: f.date };
    const n = (v) => parseFloat(v) || 0;
    if (kind === "work") {
      if (!f.work.trim()) return { error: "Enter the work to be completed" };
      Object.assign(body, { work: f.work, description: f.description, parts_used: f.parts_used, quantity: n(f.quantity), helper_ids: helperIds });
    } else if (kind === "parts") {
      if (!f.parts_used.trim()) return { error: "Enter the part used" };
      Object.assign(body, { parts_used: f.parts_used, quantity: n(f.quantity), description: f.description, part_id: f.part_id, part_number: f.part_number, supplier: f.supplier, unit_cost: n(f.unit_cost) });
    } else if (kind === "hours") {
      if (!(parseFloat(f.hours) > 0)) return { error: "Enter hours for the day" };
      if (wantsPerDiem && numPerDiems <= 0) return { error: "Enter the number of per diems" };
      Object.assign(body, { hours: n(f.hours), num_techs: numTechs, tech_ids: techIds, tech_names: extraNames, per_diem: wantsPerDiem, num_per_diems: wantsPerDiem ? numPerDiems : 0, task_id: f.task_id });
    } else if (kind === "expenses") {
      if (!f.description.trim()) return { error: "Enter an expense title" };
      Object.assign(body, { description: f.description, details: f.details, cost: n(f.cost) });
    } else if (kind === "hardware") {
      if (!f.description.trim()) return { error: "Enter the hardware used" };
      Object.assign(body, { description: f.description, quantity: n(f.quantity) });
    } else {
      if (!f.note.trim()) return { error: "Enter a note" };
      Object.assign(body, { note: f.note });
    }
    return { body };
  };

  const persist = async () => {
    const built = build();
    if (built.error) throw new Error(built.error);
    const multi = !eid && (kind === "hours" || kind === "expenses");
    const dates = [...new Set(multi ? [f.date, ...extraDates.filter(Boolean)] : [f.date])];
    let res;
    for (const date of dates) {
      res = await api(`/work-orders/${wid}/entries/${kind}${eid ? `/${eid}` : ""}`, { method: eid ? "PUT" : "POST", body: { ...built.body, date } });
    }
    refresh(res);
    if (kind === "hours") qc.invalidateQueries({ queryKey: ["project"] });
    return dates.length;
  };

  const save = async (another) => {
    setSaving(true);
    try {
      const n = await persist();
      if (!eid) draft.clear();
      if (another) {
        if (kind === "parts") setF((s) => ({ ...s, parts_used: "", quantity: "1", description: "", part_id: "", part_number: "", supplier: "", unit_cost: "" }));
        else setF((s) => ({ ...s, description: "", details: "", cost: "" }));
        toast.success(`${kind === "parts" ? "Part" : "Expense"} added — enter the next one`);
      } else {
        toast.success(eid ? "Entry updated" : n > 1 ? `${n} ${kind === "hours" ? "days" : "expenses"} logged` : "Entry added");
        nav(-1);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const draftData = useMemo(() => ({ f, techIds, extraDates, helperIds }), [f, techIds, extraDates, helperIds]);
  const draft = useDraft(`draft:entry:${wid}:${kind}${task ? `:${task}` : ""}`, draftData,
    (d) => { setF((s) => ({ ...s, ...d.f })); setTechIds(d.techIds ?? []); setExtraDates(d.extraDates ?? []); setHelperIds(d.helperIds ?? []); }, !eid);
  const auto = useAutoSave(draftData, () => persist(), { ready: !!eid && ready, enabled: !build().error });

  const dateField = <Field label={!eid && (kind === "hours" || kind === "expenses") ? "Date(s)" : "Date"} type="date" testid="entry-date-input" value={f.date} onChange={set("date")} />;
  const extraDatesUI = !eid && (
    <div className="mb-4">
      {extraDates.map((d, i) => (
        <div key={i} className="flex items-end gap-2">
          <Field className="flex-1" label={`Additional Date ${i + 1}`} type="date" testid={`entry-extra-date-${i}`} value={d} onChange={(v) => setExtraDates((ds) => ds.map((x, j) => (j === i ? v : x)))} />
          <button data-testid={`entry-extra-date-remove-${i}`} onClick={() => setExtraDates((ds) => ds.filter((_, j) => j !== i))} className="mb-5 rounded-full p-2 text-wf-mute hover:bg-wf-s3"><X size={18} /></button>
        </div>
      ))}
      <Btn variant="secondary" icon={CalendarPlus} size="sm" onClick={() => setExtraDates((ds) => [...ds, ""])} data-testid="entry-add-date-button">Add another date</Btn>
      {extraDates.length > 0 && <p className="mt-2 text-xs text-wf-mute">{kind === "hours" ? "The same hours, technicians and per diems are logged for every date." : "The same expense is logged for every date."}</p>}
    </div>
  );

  return (
    <Page title={`${eid ? "Edit · " : ""}${TITLES[kind] ?? "Entry"}`} subtitle={wo.data?.customer?.name} back testid="entry-form"
      right={eid ? <><SaveStatus status={auto.status} /><Btn variant="secondary" onClick={() => nav(-1)} data-testid="entry-done-button">Done</Btn></>
        : <Btn icon={Save} loading={saving} onClick={() => save(false)} data-testid="entry-save-button">Add Entry</Btn>}>
      <DraftBanner draft={draft} onDiscard={() => { draft.clear(); setF(blank(kind, task)); setTechIds([]); setExtraDates([]); setHelperIds([]); }} />
      <div className="max-w-2xl">
        {kind === "work" && (<>
          {dateField}
          <Field label="Work To Be Completed *" testid="entry-work-input" value={f.work} onChange={set("work")} placeholder="Install card readers on floor 3" />
          <Field label="Description" testid="entry-description-input" value={f.description} onChange={set("description")} multiline />
          <Section title="Helpers" />
          <p className="mb-2 text-sm text-wf-mute">Helpers can open this job and see its details.</p>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="entry-helpers">
            {(people.data ?? []).filter((u) => u.id !== user?.id || helperIds.includes(u.id)).map((u) => (
              <Chip key={u.id} testid={`entry-helper-${u.id}`} label={u.name} selected={helperIds.includes(u.id)}
                onClick={() => setHelperIds((ids) => (ids.includes(u.id) ? ids.filter((x) => x !== u.id) : [...ids, u.id]))} />
            ))}
          </div>
        </>)}
        {kind === "parts" && (<>
          {dateField}
          <PartPick parts={parts.data} onPick={(p) => setF((s) => ({ ...s, part_id: p.id, parts_used: p.name, part_number: p.part_number, supplier: p.supplier, unit_cost: p.cost ? String(p.cost) : "" }))} />
          <Field label="Part Name *" testid="entry-parts-input" value={f.parts_used} onChange={set("parts_used")} placeholder="HID iCLASS reader" />
          <div className="grid gap-x-3 sm:grid-cols-2">
            <Field label="Part Number" testid="entry-part-number-input" value={f.part_number} onChange={set("part_number")} />
            <SuggestField suggestions={sSupplier.data} label="Supplier" testid="entry-supplier-input" value={f.supplier} onChange={set("supplier")} />
            <Field label="Quantity Used" type="number" testid="entry-quantity-input" value={f.quantity} onChange={set("quantity")} />
            {seeMoney && <Field label="Unit Cost ($)" type="number" step="0.01" testid="entry-unit-cost-input" value={f.unit_cost} onChange={set("unit_cost")} />}
          </div>
          {seeMoney && <Preview testid="parts-preview" items={[["Parts Total", money((parseFloat(f.quantity) || 0) * (parseFloat(f.unit_cost) || 0))]]} />}
          <Field label="Notes (optional)" testid="entry-description-input" value={f.description} onChange={set("description")} />
          {!eid && <Btn variant="secondary" icon={PlusCircle} loading={saving} onClick={() => save(true)} data-testid="entry-add-another-part-button">Save & Add Another Part</Btn>}
        </>)}
        {(kind === "notes" || kind === "daily_notes") && (<>
          {dateField}
          <Field label={kind === "notes" ? "Monthly Note *" : "Note for the day *"} testid="entry-note-input" value={f.note} onChange={set("note")} multiline />
        </>)}
        {kind === "hours" && (<>
          {dateField}
          {extraDatesUI}
          {!!project.data?.tasks.length && (
            <Select label="Project Item (optional)" testid="entry-task-dropdown" value={f.task_id} placeholder="None" options={project.data.tasks.map((t) => ({ value: t.id, label: t.title }))} onChange={set("task_id")} />
          )}
          <Field label="Hours for the Day *" type="number" step="0.25" testid="entry-hours-input" value={f.hours} onChange={set("hours")} placeholder="8" />
          <Section title="Onsite Technicians" />
          {(emps.data ?? []).length === 0 ? <p className="text-sm text-wf-mute">No employees yet — add them in Team to auto-calculate rates.</p> : (
            <div className="mb-4 flex flex-wrap gap-2">
              {(emps.data ?? []).filter((e) => (e.status ?? "active") === "active" || techIds.includes(e.id)).map((e) => (
                <Chip key={e.id} testid={`entry-tech-${e.id}`} label={seeMoney ? `${e.name} · ${money(e.hourly_rate)}` : e.name} selected={techIds.includes(e.id)}
                  onClick={() => setTechIds((ids) => (ids.includes(e.id) ? ids.filter((x) => x !== e.id) : [...ids, e.id]))} />
              ))}
            </div>
          )}
          <Field label="Other technicians (names, comma separated)" testid="entry-extra-names-input" value={f.extra_names} onChange={set("extra_names")} />
          <Field label="Number of Technicians" type="number" testid="entry-num-techs-input" value={f.num_techs} onChange={set("num_techs")} placeholder={String(autoTechs || 1)} />
          <p className="mb-2 text-[13px] font-semibold">Per Diem Needed?</p>
          <div className="mb-4 flex gap-2">
            <Chip testid="entry-per-diem-no" label="No" selected={!wantsPerDiem} onClick={() => setF((s) => ({ ...s, per_diem: "no" }))} />
            <Chip testid="entry-per-diem-yes" label="Yes" selected={wantsPerDiem} onClick={() => setF((s) => ({ ...s, per_diem: "yes", num_per_diems: s.num_per_diems || String(numTechs || 1) }))} />
          </div>
          {wantsPerDiem && <Field label="Number of Per Diems" type="number" testid="entry-num-per-diems-input" value={f.num_per_diems} onChange={set("num_per_diems")} />}
          <Preview testid="hours-preview" items={[["Man Hours", num(preview.manHours)], ...(seeMoney ? [["Labor Cost", money(preview.cost)]] : [])]} />
          {wantsPerDiem && seeMoney && <Preview testid="per-diem-preview" items={[["Per Diems", money(numPerDiems * perDiemRate)], [`${numPerDiems} × ${money(perDiemRate)}`, ""]]} />}
          {seeMoney && <p className="text-sm text-wf-mute">Labor cost = hours × each technician's hourly rate.</p>}
        </>)}
        {kind === "expenses" && (<>
          {dateField}
          {extraDatesUI}
          <SuggestField suggestions={sExpense.data} label="Expense Title *" testid="entry-description-input" value={f.description} onChange={set("description")} placeholder="Parking, lift rental..." />
          <Field label="Expense Description" testid="entry-details-input" value={f.details} onChange={set("details")} multiline />
          {(seeMoney || !eid) && <Field label="Daily Expense Cost ($)" type="number" step="0.01" testid="entry-cost-input" value={f.cost} onChange={set("cost")} placeholder="0.00" />}
          {!eid && <Btn variant="secondary" icon={PlusCircle} loading={saving} onClick={() => save(true)} data-testid="entry-add-another-button">Save & Add Another Expense</Btn>}
        </>)}
        {kind === "hardware" && (<>
          {dateField}
          <PartPick parts={parts.data} onPick={(p) => setF((s) => ({ ...s, description: p.name }))} />
          <SuggestField suggestions={[...(sHardware.data ?? []), ...(parts.data ?? []).map((p) => p.name)]} label="Extra Hardware Used *" testid="entry-description-input" value={f.description} onChange={set("description")} />
          <Field label="Quantity of Parts" type="number" testid="entry-quantity-input" value={f.quantity} onChange={set("quantity")} />
        </>)}
      </div>
    </Page>
  );
}
